/**
 * CHANVRERY — FIDÉLITÉ, LES SECTIONS PUBLIQUES DU PROGRAMME
 *
 * ─── POURQUOI UN SECOND SCRIPT, ET PAS UN AJOUT DANS ch-fidelite.js ─────────
 *
 * `ch-fidelite.js` sort dès sa deuxième ligne quand `[data-ch-fid-proxy]` est
 * absent — et cet attribut ne vit que dans la branche `{% if customer %}` de
 * l'entête. Il ne s'exécute donc JAMAIS pour un visiteur non connecté.
 *
 * Or ces deux sections s'adressent exactement à ce visiteur-là : elles
 * expliquent le programme à quelqu'un qui n'y est pas encore inscrit. Les
 * greffer sur l'autre script aurait demandé d'en desserrer la garde d'entrée,
 * donc de faire tourner tout le code de la carte — solde, historique, échange —
 * pour des gens qui n'ont rien à y voir.
 *
 * Deux scripts, deux publics, aucune dépendance entre eux.
 *
 * ─── CE QU'IL NE FAIT PAS ───────────────────────────────────────────────────
 *
 * Il n'affiche RIEN de personnel : `/api/programme` ne rend que le barème, et
 * la route ne demande aucune identité. Ce fichier ne doit jamais servir à autre
 * chose — le jour où une donnée nominative serait nécessaire ici, c'est la
 * route qu'il faudrait changer, pas ce script.
 */
(() => {
  'use strict';

  // Les deux sections émettent ce script. Le navigateur ne télécharge le
  // fichier qu'une fois, mais l'exécuterait deux fois : ce drapeau garantit un
  // seul passage, et donc un seul appel réseau pour les deux.
  if (window.chFideliteProgrammeChargee) return;

  const sections = document.querySelectorAll('[data-ch-prog-section]');
  if (!sections.length) return;

  window.chFideliteProgrammeChargee = true;

  const LANGUE = document.documentElement.lang || 'fr';

  /** Toutes les sections partagent le même proxy ; la première fait foi. */
  const PROXY = sections[0].dataset.chProgProxy || '/apps/fidelite';

  const nombre = (n) => new Intl.NumberFormat(LANGUE).format(n || 0);

  /**
   * Un montant en centimes, rendu sans décimales quand il n'en a pas besoin.
   *
   * Les seuils de palier sont des ronds — 350 €, 900 € — et « 350,00 € » sur
   * une carte de présentation fait comptable. `minimumFractionDigits: 0` ne
   * suffit pas seul : il faut aussi borner le maximum, sinon 349,50 € passerait
   * à 349,5 €.
   */
  const euros = (centimes) => {
    const v = (centimes || 0) / 100;
    const entier = Number.isInteger(v);
    return new Intl.NumberFormat(LANGUE, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: entier ? 0 : 2,
      maximumFractionDigits: entier ? 0 : 2,
    }).format(v);
  };

  const gabarit = (modele, valeurs) =>
    Object.keys(valeurs).reduce(
      (texte, cle) => texte.split('{' + cle + '}').join(valeurs[cle]),
      modele || '',
    );

  /**
   * `section` EST déjà le conteneur — c'est lui qui porte
   * `data-ch-prog-section`, et c'est lui que la feuille de style cible par
   * `[data-ch-prog-etat]`. Pas de descendant à chercher.
   */
  const etat = (section, valeur) => {
    section.dataset.chProgEtat = valeur;
  };

  /**
   * Quelle icône pour quelle mission.
   *
   * ⚠️ Le barème est une DONNÉE : ses codes de mission peuvent changer, et il en
   * existe déjà un — `review_site` — que la maquette n'avait pas prévu. Une
   * mission inconnue reçoit donc l'icône par défaut plutôt que rien : une tuile
   * sans pastille casserait l'alignement de toute la grille.
   *
   * `wallet_added` et `referral` figurent dans cette table sans exister au
   * barème : la maquette les dessinait, et ils pourraient revenir. Les prévoir
   * ne coûte rien ; les découvrir en production coûterait une tuile muette.
   */
  const ICONES = {
    account_created: 'user',
    newsletter_signup: 'mail',
    birthdate_provided: 'calendar',
    review_product: 'star',
    review_site: 'heart',
    telegram_joined: 'share-2',
    wallet_added: 'qr-code',
    referral: 'share-2',
  };
  const ICONE_DEFAUT = 'star';

  /** Clone l'icône préparée en Liquid, ou rend `null` si elle manque. */
  const icone = (section, nom) => {
    const source = section.querySelector('[data-ch-prog-svg="' + nom + '"] svg');
    return source ? source.cloneNode(true) : null;
  };

  // -------------------------------------------------------------------------
  // « Gagner des points »
  // -------------------------------------------------------------------------

  const remplirGagner = (section, programme) => {
    const grille = section.querySelector('[data-ch-prog-grille]');
    const modele = section.querySelector('[data-ch-prog-gabarit]');
    if (!grille || !modele) return;

    /**
     * La première tuile n'est pas une mission : c'est le TAUX de base.
     *
     * Il n'est pas servi tel quel par l'API — il se déduit du multiplicateur du
     * premier palier, sachant qu'un euro vaut un point à ×1 (`record_purchase`
     * dans la migration 0002 : `round(amount_cents × multiplier / 100)`).
     * Le calculer ici plutôt que de l'écrire évite de figer « 1 point » le jour
     * où le premier palier passerait à ×1,5.
     */
    const base = programme.tiers && programme.tiers[0];
    const parEuro = base && typeof base.multiplier === 'number' ? base.multiplier : 1;

    const tuiles = [
      {
        gain: nombre(parEuro) + (parEuro > 1 ? ' pts' : ' pt') + ' par €',
        libelle: grille.dataset.libelleAchat || 'Chaque achat, en boutique et en ligne',
        icone: 'credit-card',
      },
    ].concat(
      (programme.missions || []).map((m) => ({
        gain: '+' + nombre(m.points) + (m.points > 1 ? ' pts' : ' pt'),
        libelle: m.label,
        icone: ICONES[m.code] || ICONE_DEFAUT,
      })),
    );

    const fragment = document.createDocumentFragment();

    tuiles.forEach((t) => {
      const carte = modele.content.firstElementChild.cloneNode(true);

      const pastille = carte.querySelector('[data-ch-prog-icone]');
      const svg = icone(section, t.icone);
      if (pastille && svg) pastille.appendChild(svg);

      const gain = carte.querySelector('[data-ch-prog-gain]');
      if (gain) gain.textContent = t.gain;

      const libelle = carte.querySelector('[data-ch-prog-libelle]');
      if (libelle) libelle.textContent = t.libelle;

      fragment.appendChild(carte);
    });

    grille.replaceChildren(fragment);

    // Le taux dans la mention de pied. `{taux}` est le seul jeton : le marchand
    // écrit la phrase, on n'y injecte que ce qu'on sait.
    const pied = section.querySelector('[data-ch-prog-pied]');
    if (pied) {
      pied.textContent = gabarit(pied.textContent, {
        taux: nombre(parEuro) + (parEuro > 1 ? ' points' : ' point'),
      });
    }
  };

  // -------------------------------------------------------------------------
  // « Les paliers »
  // -------------------------------------------------------------------------

  const remplirPaliers = (section, programme) => {
    const hote = section.querySelector('[data-ch-prog-paliers]');
    const modele = section.querySelector('[data-ch-prog-gabarit-palier]');
    if (!hote || !modele) return;

    const paliers = programme.tiers || [];
    if (!paliers.length) return;

    // Les avantages saisis par le marchand, regroupés par code de palier.
    const avantages = {};
    const source = section.querySelector('[data-ch-prog-avantages]');
    if (source) {
      source.content.querySelectorAll('li[data-palier]').forEach((li) => {
        const code = li.dataset.palier;
        (avantages[code] = avantages[code] || []).push(li.textContent);
      });
    }

    const fragment = document.createDocumentFragment();

    paliers.forEach((palier, index) => {
      const carte = modele.content.firstElementChild.cloneNode(true);

      // Le dernier de la liste triée par seuil est mis en avant. La règle vaut
      // pour deux paliers comme pour cinq.
      if (index === paliers.length - 1) {
        carte.classList.add('ch-fid-prog__palier--sommet');
      }

      const nom = carte.querySelector('[data-ch-prog-nom]');
      if (nom) nom.textContent = palier.label;

      // ⚠️ Un seuil de 0 n'est pas « dès 0 € » : c'est le palier d'entrée, celui
      // qu'on a en s'inscrivant. L'écrire en euros donnerait une phrase absurde
      // sur la première carte.
      const seuil = carte.querySelector('[data-ch-prog-seuil]');
      if (seuil) {
        seuil.textContent =
          palier.thresholdCents > 0
            ? (hote.dataset.gabaritSeuil || 'Dès {montant} dépensés').replace(
                '{montant}',
                euros(palier.thresholdCents),
              )
            : hote.dataset.libelleEntree || "Dès l'inscription";
      }

      const liste = carte.querySelector('[data-ch-prog-liste]');
      const lignes = avantages[palier.code] || [];
      if (liste && lignes.length) {
        lignes.forEach((texte) => {
          const li = document.createElement('li');
          const svg = icone(section, 'check');
          if (svg) li.appendChild(svg);
          const span = document.createElement('span');
          span.textContent = texte;
          li.appendChild(span);
          liste.appendChild(li);
        });
      }

      fragment.appendChild(carte);
    });

    hote.replaceChildren(fragment);
  };

  // -------------------------------------------------------------------------
  // Chargement
  // -------------------------------------------------------------------------

  sections.forEach((s) => etat(s, 'charge'));

  fetch(PROXY + '/api/programme', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((reponse) => {
      if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
      return reponse.json();
    })
    .then((programme) => {
      sections.forEach((section) => {
        const quoi = section.dataset.chProgSection;
        if (quoi === 'gagner') remplirGagner(section, programme);
        if (quoi === 'paliers') remplirPaliers(section, programme);
        etat(section, 'prete');
      });
    })
    .catch((cause) => {
      // La section entière disparaît — la feuille de style s'en charge. Un titre
      // « Gagner des points » suivi de rien se lit comme une page cassée, et ces
      // sections n'ont aucun contenu propre qui vaudrait d'être conservé.
      console.error('[fidélité] barème indisponible', cause);
      sections.forEach((s) => etat(s, 'panne'));
    });
})();
